import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Loader2, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { PublicFooter } from '../components/PublicFooter';

export const LegalDocument = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const contentRef = useRef(null);
  
  // Normalize 'legal' to 'terms' for backward compatibility
  const normalizedType = type === 'legal' ? 'terms' : type;

  useEffect(() => {
    // Helper function to get document URL based on language and type
    const getDocumentUrl = () => {
      const basePath = '/gizlilksozlesmesi';
      const fileMap = {
        privacy: {
          tr: `${basePath}/gizlilik-politikasi.md`,
          en: `${basePath}/privacy-policy.md`,
          de: `${basePath}/datenschutzerklaerung-zeuz-it.md`,
          es: `${basePath}/politica-de-privacidad.md`,
          fr: `${basePath}/politique-de-confidentialite.md`,
        },
        terms: {
          tr: `${basePath}/kullanim-sartlari.md`,
          en: `${basePath}/terms-of-service.md`,
          de: `${basePath}/impressum-zeuz-it.md`,
          es: `${basePath}/terminos-de-servicio.md`,
          fr: `${basePath}/conditions-d-utilisation.md`,
        },
      };
      
      // Get the URL for current language, fallback to English if not available
      const url = fileMap[normalizedType]?.[language] || fileMap[normalizedType]?.en;
      return url;
    };

    const loadDocument = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const url = getDocumentUrl();
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('Document not found');
        }
        
        const text = await response.text();
        setContent(text);
        // Trigger animation after content is loaded
        setTimeout(() => {
          setIsVisible(true);
        }, 100);
      } catch (err) {
        setError(err.message);
        // Try to load English version as fallback
        if (language !== 'en') {
          try {
            const fallbackUrl = type === 'privacy' 
              ? '/gizlilksozlesmesi/privacy-policy.md'
              : '/gizlilksozlesmesi/terms-of-service.md';
            const response = await fetch(fallbackUrl);
            if (response.ok) {
              const text = await response.text();
              setContent(text);
              setError(null);
              setTimeout(() => {
                setIsVisible(true);
              }, 100);
            }
          } catch (fallbackErr) {
            // Keep the original error
          }
        }
      } finally {
        setLoading(false);
      }
    };

    if (type) {
      loadDocument();
    }
  }, [type, language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600 dark:text-cyan-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        {/* Unknown /legal/:type must never be indexed — it is an error state, not a page. */}
        <Helmet>
          <title>Document Not Found - FeedPanels</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Document Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const getDocumentTitle = () => {
    if (normalizedType === 'privacy') {
      return 'Privacy Policy - FeedPanels';
    } else if (normalizedType === 'terms') {
      return 'Terms of Service - FeedPanels';
    }
    return 'Legal Document - FeedPanels';
  };

  const getDocumentDescription = () => {
    if (normalizedType === 'privacy') {
      return 'Read FeedPanels privacy policy. Learn how we collect, use, and protect your personal information.';
    } else if (normalizedType === 'terms') {
      return 'Read FeedPanels terms of service. Understand the rules and guidelines for using our platform.';
    }
    return 'Legal documents for FeedPanels platform.';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-300 flex flex-col">
      <Helmet>
        <title>{getDocumentTitle()}</title>
        <meta name="description" content={getDocumentDescription()} />
        <link rel="canonical" href={`https://feedpanels.com/legal/${normalizedType}`} />
      </Helmet>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur border-b dark:border-gray-700 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img 
                src="/logo-256.webp" 
                alt="FeedPanels Logo" 
                className="w-28 h-28 sm:w-32 sm:h-32 object-contain"
              />
              <div>
                <p className="text-base sm:text-xl font-bold text-gray-900 dark:text-white" aria-label="FeedPanels">FeedPanels</p>
              </div>
            </div>
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Document Header */}
          <div 
            className={`mb-8 transition-all duration-700 ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-4'
            }`}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
                  {normalizedType === 'privacy' 
                    ? (language === 'tr' ? 'Gizlilik Politikası' : 
                       language === 'de' ? 'Datenschutzerklärung' : 
                       language === 'es' ? 'Política de Privacidad' : 
                       language === 'fr' ? 'Politique de Confidentialité' : 
                       'Privacy Policy')
                    : (language === 'tr' ? 'Kullanım Şartları' : 
                       language === 'de' ? 'Impressum' : 
                       language === 'es' ? 'Términos de Servicio' : 
                       language === 'fr' ? 'Conditions d\'Utilisation' : 
                       'Terms of Service')
                  }
                </h1>
                <p className="text-lg leading-8 mb-6 font-bold opacity-100 text-muted-foreground">
                  {language === 'tr' ? 'Son güncelleme: Aralık 2025' : 
                   language === 'de' ? 'Stand: Dezember 2025' : 
                   language === 'es' ? 'Última actualización: Diciembre 2025' : 
                   language === 'fr' ? 'Dernière mise à jour : Décembre 2025' : 
                   'Last updated: December 2025'}
                </p>
              </div>
            </div>
          </div>

          {/* Document Content */}
          <div 
            ref={contentRef}
            className={`bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-gray-900/50 p-6 sm:p-8 md:p-10 lg:p-12 transition-all duration-700 delay-200 ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="legal-document prose prose-xl dark:prose-invert max-w-none
              prose-headings:font-extrabold prose-headings:scroll-mt-20 prose-headings:tracking-tight
              prose-h1:text-5xl dark:prose-h1:text-white prose-h1:mb-12 prose-h1:mt-0 prose-h1:leading-tight
              prose-h2:text-4xl dark:prose-h2:text-white prose-h2:mt-20 prose-h2:mb-8 prose-h2:pt-6 prose-h2:pb-4 prose-h2:border-b-2 prose-h2:border-gray-300 dark:prose-h2:border-gray-600 prose-h2:font-bold prose-h2:text-gray-900 dark:prose-h2:text-white
              prose-h3:text-3xl dark:prose-h3:text-white prose-h3:mt-16 prose-h3:mb-6 prose-h3:font-bold prose-h3:text-gray-800 dark:prose-h3:text-gray-100
              prose-h4:text-2xl dark:prose-h4:text-white prose-h4:mt-12 prose-h4:mb-5 prose-h4:font-semibold prose-h4:text-gray-800 dark:prose-h4:text-gray-200
              prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-8 prose-p:text-lg prose-p:font-normal
              prose-a:text-cyan-600 dark:prose-a:text-cyan-400 prose-a:no-underline hover:prose-a:underline prose-a:font-semibold prose-a:transition-all
              prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-bold prose-strong:text-xl
              prose-code:text-gray-800 dark:prose-code:text-gray-200 prose-code:bg-gray-100 dark:prose-code:bg-gray-900 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-base prose-code:font-mono prose-code:border prose-code:border-gray-200 dark:prose-code:border-gray-700
              prose-pre:bg-gray-100 dark:prose-pre:bg-gray-900 prose-pre:border-2 prose-pre:border-gray-200 dark:prose-pre:border-gray-700 prose-pre:rounded-lg prose-pre:p-6 prose-pre:overflow-x-auto prose-pre:my-8
              prose-blockquote:border-l-4 prose-blockquote:border-l-cyan-600 dark:prose-blockquote:border-l-cyan-400 prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:bg-cyan-50 dark:prose-blockquote:bg-cyan-900/20 prose-blockquote:py-4 prose-blockquote:my-8 prose-blockquote:rounded-r-lg prose-blockquote:border-r prose-blockquote:border-t prose-blockquote:border-b prose-blockquote:border-gray-200 dark:prose-blockquote:border-gray-700
              prose-hr:border-2 prose-hr:border-gray-300 dark:prose-hr:border-gray-700 prose-hr:my-16
              prose-ul:list-disc prose-ul:pl-10 prose-ul:my-8 prose-ul:space-y-4
              prose-ol:list-decimal prose-ol:pl-10 prose-ol:my-8 prose-ol:space-y-4
              prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-li:my-3 prose-li:leading-relaxed prose-li:pl-3 prose-li:text-lg
              prose-table:w-full prose-table:my-10 prose-table:border-collapse
              prose-th:px-6 prose-th:py-4 prose-th:text-left prose-th:font-bold prose-th:text-gray-900 dark:prose-th:text-white prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600
              prose-td:px-6 prose-td:py-4 prose-td:border prose-td:border-gray-200 dark:prose-td:border-gray-700 prose-td:text-gray-700 dark:prose-td:text-gray-300">
              <div className="animate-fade-in">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {content}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

